import React from 'react';
import { useApp } from '../../context/AppContext.tsx';
import {
  Layers,
  Phone,
  Mail,
  MapPin,
  Rss,
  FileCode2,
  Lock,
  ArrowUpRight,
  ShieldCheck,
  Instagram,
  Youtube,
  Facebook,
  Linkedin,
  MessageCircle,
  ExternalLink,
} from 'lucide-react';
import { FooterLinkItem, SocialLinksConfig } from '../../types.ts';

// Custom SVG Icons for TikTok and Twitter/X
export const TikTokIcon = ({ className = 'w-4 h-4' }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M19.589 6.686a4.793 4.793 0 0 1-3.77-4.245V2h-3.445v13.672a2.896 2.896 0 1 1-2.901-2.895c.138 0 .273.01.405.029V9.337a6.332 6.332 0 0 0-.405-.013 6.34 6.34 0 1 0 6.34 6.34V8.756a8.238 8.238 0 0 0 4.767 1.488V6.799a4.814 4.814 0 0 1-1.001-.113z" />
  </svg>
);

export const TwitterXIcon = ({ className = 'w-4 h-4' }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

export default function Footer() {
  const { settings, navigate, categories, user } = useApp();

  const hasSecretConfig = Boolean(settings?.security?.customLoginPath?.trim());
  const hideLoginBtn = hasSecretConfig || settings?.security?.hidePublicLoginButtons;

  const primaryColor = settings?.theme?.primaryColor || '#059669';

  const footerConfig = settings?.footerConfig || {};
  const socialConfig: SocialLinksConfig = footerConfig.socialLinks || {};

  // Background color & styling
  const bgColor = footerConfig.bgColor || footerConfig.backgroundColor || '#020617';
  const textColor = footerConfig.textColor || '#94a3b8';

  // Visibility toggles
  const showSocial = footerConfig.showSocial !== false && footerConfig.showSocialLinks !== false;
  const showQuickLinks = footerConfig.showQuickLinks !== false;
  const showCategories = footerConfig.showCategories !== false;
  const showContact = footerConfig.showContact !== false && footerConfig.showContactInfo !== false;
  const showSeoLinks = footerConfig.showSeoLinks !== false;
  const showAdminLink = footerConfig.showAdminLink !== false;
  const showWaChatBtn = footerConfig.showWaChatBtn !== false;
  const waChatBtnText = footerConfig.waChatBtnText || 'Chat With Specialist';

  // Header Titles & Texts
  const aboutTitle =
    footerConfig.aboutTitle?.trim() ||
    settings?.siteName ||
    'ALY LAW FIRM';
  const aboutText =
    footerConfig.aboutText ||
    settings?.seo?.metaDescription ||
    'ALY LAW FIRM adalah kantor advokat dan konsultan hukum independen yang berdedikasi memberikan pendampingan hukum terbaik.';
  const navTitle = footerConfig.navTitle || 'Navigasi Website';
  const categoriesTitle = footerConfig.categoriesTitle || 'Kategori & Topik';
  const contactTitle = footerConfig.contactTitle || 'Kontak & Alamat Kantor';
  const copyrightText =
    footerConfig.copyrightText ||
    `${settings?.siteName || 'ALY LAW FIRM'}. All rights reserved.`;

  // Social Links List with fallbacks from settings.contact
  const instagramUrl = socialConfig.instagram || settings?.contact?.instagram || '';
  const youtubeUrl = socialConfig.youtube || settings?.contact?.youtube || '';
  const facebookUrl = socialConfig.facebook || settings?.contact?.facebook || '';
  const tiktokUrl = socialConfig.tiktok || settings?.contact?.tiktok || '';
  const linkedinUrl = socialConfig.linkedin || settings?.contact?.linkedin || '';
  const twitterUrl = socialConfig.twitter || settings?.contact?.twitter || '';
  const whatsappVal = socialConfig.whatsapp || settings?.contact?.whatsappNumber || '';

  const formattedWaUrl = whatsappVal
    ? whatsappVal.startsWith('http')
      ? whatsappVal
      : `https://wa.me/${whatsappVal.replace(/[^0-9]/g, '')}`
    : '';

  // Quick Links
  const quickLinks: FooterLinkItem[] =
    footerConfig.quickLinks && footerConfig.quickLinks.length > 0
      ? footerConfig.quickLinks
      : footerConfig.customLinks && footerConfig.customLinks.length > 0
      ? footerConfig.customLinks.map((cl, i) => ({
          id: cl.id || `cl-${i}`,
          label: cl.label,
          targetType: 'url' as const,
          customUrl: cl.url,
          openNewTab: cl.openInNewTab,
        }))
      : [
          { id: 'fl-1', label: 'Homepage', targetType: 'page', pageTarget: 'home' },
          { id: 'fl-2', label: 'Services & Solutions', targetType: 'page', pageTarget: 'products' },
          { id: 'fl-3', label: 'Case Studies & Portfolio', targetType: 'page', pageTarget: 'portfolio' },
          { id: 'fl-4', label: 'Articles & Insights', targetType: 'page', pageTarget: 'blog' },
          { id: 'fl-5', label: 'Media & Gallery', targetType: 'page', pageTarget: 'gallery' },
          { id: 'fl-6', label: 'About Us', targetType: 'page', pageTarget: 'about' },
          { id: 'fl-7', label: 'Contact & Advisory', targetType: 'page', pageTarget: 'contact' },
        ];

  const handleLinkClick = (link: FooterLinkItem) => {
    if (link.targetType === 'url' || link.customUrl) {
      const url = (link.customUrl || '').trim();
      if (!url) return;
      if (link.openNewTab || url.startsWith('http://') || url.startsWith('https://')) {
        window.open(url, '_blank', 'noopener,noreferrer');
        return;
      }
      if (url === '/' || url === '/home') navigate('home');
      else if (url === '/about') navigate('about');
      else if (url === '/contact') navigate('contact');
      else if (url === '/produk' || url === '/products') navigate('products');
      else if (url === '/portofolio' || url === '/portfolio') navigate('portfolio');
      else if (url === '/artikel' || url === '/blog') navigate('blog');
      else if (url === '/galeri' || url === '/gallery') navigate('gallery');
      else if (url.startsWith('/page/')) navigate('custom-page', url.replace('/page/', ''));
      else if (url.startsWith('/artikel/')) navigate('article-detail', url.replace('/artikel/', ''));
      else if (url.startsWith('/produk/')) navigate('product-detail', url.replace('/produk/', ''));
      else window.location.href = url;
      return;
    }

    if (link.pageTarget) {
      navigate(link.pageTarget);
    }
  };

  return (
    <footer
      id="site-footer"
      className="border-t border-slate-900/60 pt-12 sm:pt-16 pb-12 transition-colors"
      style={{ backgroundColor: bgColor }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-8 lg:gap-8 pb-8 sm:pb-10 border-b border-slate-800/80">
          {/* Col 1: Identity & Intro & Socials */}
          <div className="lg:col-span-4 space-y-4">
            <div
              className="flex items-center gap-3 cursor-pointer group"
              onClick={() => navigate('home')}
              title={aboutTitle || settings?.siteName || 'Home'}
            >
              {settings?.logoUrl ? (
                <img
                  src={settings.logoUrl}
                  alt={aboutTitle || settings?.siteName || 'Logo'}
                  className="h-9 sm:h-11 w-auto max-h-12 max-w-[240px] object-contain transition-transform group-hover:scale-105"
                />
              ) : (
                <>
                  <div
                    className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl flex items-center justify-center text-white font-bold text-lg shadow-md shrink-0"
                    style={{ backgroundColor: primaryColor }}
                  >
                    <Layers className="w-5 h-5" />
                  </div>
                  <span
                    className="font-extrabold text-lg sm:text-xl text-white tracking-tight leading-snug"
                    style={{ fontFamily: 'var(--font-heading)' }}
                  >
                    {aboutTitle}
                  </span>
                </>
              )}
            </div>

            {aboutText && (
              <p
                className="text-xs sm:text-sm leading-relaxed max-w-sm"
                style={{ color: textColor }}
              >
                {aboutText}
              </p>
            )}

            {/* Social Media Buttons with Icons */}
            {showSocial && (
              <div className="pt-2">
                <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider block mb-2.5">
                  Connect With Us
                </span>
                <div className="flex flex-wrap items-center gap-2">
                  {instagramUrl && (
                    <a
                      href={instagramUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-9 h-9 rounded-xl bg-slate-900/90 hover:bg-gradient-to-tr hover:from-amber-500 hover:via-rose-500 hover:to-purple-600 text-slate-300 hover:text-white flex items-center justify-center transition-all duration-200 border border-slate-800 hover:border-transparent shadow-xs hover:scale-110"
                      aria-label="Instagram"
                      title="Instagram"
                    >
                      <Instagram className="w-4 h-4" />
                    </a>
                  )}
                  {youtubeUrl && (
                    <a
                      href={youtubeUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-9 h-9 rounded-xl bg-slate-900/90 hover:bg-red-600 text-slate-300 hover:text-white flex items-center justify-center transition-all duration-200 border border-slate-800 hover:border-transparent shadow-xs hover:scale-110"
                      aria-label="YouTube"
                      title="YouTube"
                    >
                      <Youtube className="w-4 h-4" />
                    </a>
                  )}
                  {facebookUrl && (
                    <a
                      href={facebookUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-9 h-9 rounded-xl bg-slate-900/90 hover:bg-blue-600 text-slate-300 hover:text-white flex items-center justify-center transition-all duration-200 border border-slate-800 hover:border-transparent shadow-xs hover:scale-110"
                      aria-label="Facebook"
                      title="Facebook"
                    >
                      <Facebook className="w-4 h-4" />
                    </a>
                  )}
                  {formattedWaUrl && (
                    <a
                      href={formattedWaUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-9 h-9 rounded-xl bg-slate-900/90 hover:bg-emerald-600 text-slate-300 hover:text-white flex items-center justify-center transition-all duration-200 border border-slate-800 hover:border-transparent shadow-xs hover:scale-110"
                      aria-label="WhatsApp Official"
                      title="WhatsApp Official"
                    >
                      <MessageCircle className="w-4 h-4" />
                    </a>
                  )}
                  {tiktokUrl && (
                    <a
                      href={tiktokUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-9 h-9 rounded-xl bg-slate-900/90 hover:bg-black hover:text-cyan-400 text-slate-300 flex items-center justify-center transition-all duration-200 border border-slate-800 hover:border-cyan-500/50 shadow-xs hover:scale-110"
                      aria-label="TikTok"
                      title="TikTok"
                    >
                      <TikTokIcon className="w-4 h-4" />
                    </a>
                  )}
                  {linkedinUrl && (
                    <a
                      href={linkedinUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-9 h-9 rounded-xl bg-slate-900/90 hover:bg-blue-700 text-slate-300 hover:text-white flex items-center justify-center transition-all duration-200 border border-slate-800 hover:border-transparent shadow-xs hover:scale-110"
                      aria-label="LinkedIn"
                      title="LinkedIn"
                    >
                      <Linkedin className="w-4 h-4" />
                    </a>
                  )}
                  {twitterUrl && (
                    <a
                      href={twitterUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-9 h-9 rounded-xl bg-slate-900/90 hover:bg-slate-800 text-slate-300 hover:text-white flex items-center justify-center transition-all duration-200 border border-slate-800 hover:border-slate-600 shadow-xs hover:scale-110"
                      aria-label="Twitter / X"
                      title="Twitter / X"
                    >
                      <TwitterXIcon className="w-4 h-4" />
                    </a>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Col 2: Navigation Links */}
          {showQuickLinks && (
            <div className="lg:col-span-3 space-y-3">
              <h4 className="text-xs sm:text-sm font-bold uppercase tracking-wider" style={{ color: textColor }}>
                {navTitle}
              </h4>
              <ul className="space-y-2 text-xs sm:text-sm">
                {quickLinks.map((link) => (
                  <li key={link.id}>
                    <button
                      onClick={() => handleLinkClick(link)}
                      className="hover:text-white transition-colors inline-flex items-center gap-1.5 text-left cursor-pointer group"
                      style={{ color: textColor }}
                    >
                      <span className="group-hover:translate-x-0.5 transition-transform" style={{ color: textColor }}>{link.label}</span>
                      {(link.targetType === 'url' || link.openNewTab || (link.customUrl && link.customUrl.startsWith('http'))) && (
                        <ExternalLink className="w-3 h-3 opacity-60" />
                      )}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Col 3: Categories & SEO Links */}
          {(showCategories || showSeoLinks) && (
            <div className="lg:col-span-2 space-y-4">
              {showCategories && categories.length > 0 && (
                <div>
                  <h4 className="text-xs sm:text-sm font-bold uppercase tracking-wider mb-2.5" style={{ color: textColor }}>
                    {categoriesTitle}
                  </h4>
                  <ul className="space-y-2 text-xs sm:text-sm">
                    {categories.slice(0, 8).map((cat) => (
                      <li key={cat.id}>
                        <button
                          onClick={() => navigate('blog')}
                          className="hover:text-white transition-colors inline-flex items-center gap-1.5 text-left cursor-pointer group"
                          style={{ color: textColor }}
                        >
                          <span className="group-hover:translate-x-0.5 transition-transform" style={{ color: textColor }}>{cat.name}</span>
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {showSeoLinks && (
                <div className="pt-1">
                  <h5 className="text-[11px] font-semibold uppercase tracking-wider mb-2" style={{ color: textColor }}>
                    SEO & Syndication
                  </h5>
                  <div className="flex flex-col gap-1.5 text-xs">
                    <a
                      href="/rss.xml"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 hover:underline"
                      style={{ color: textColor }}
                    >
                      <Rss className="w-3.5 h-3.5" />
                      <span>RSS 2.0 Feed</span>
                    </a>
                    <a
                      href="/sitemap.xml"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 hover:underline"
                      style={{ color: textColor }}
                    >
                      <FileCode2 className="w-3.5 h-3.5" />
                      <span>XML Sitemap</span>
                    </a>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Col 4: Contact & Office */}
          {showContact && (
            <div className="lg:col-span-3 space-y-3 text-xs sm:text-sm">
              <h4 className="text-xs sm:text-sm font-bold uppercase tracking-wider" style={{ color: textColor }}>
                {contactTitle}
              </h4>
              {settings?.contact?.address && (
                <p className="flex items-start gap-2 text-xs leading-relaxed" style={{ color: textColor }}>
                  <MapPin className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" />
                  <span style={{ color: textColor }}>{settings.contact.address}</span>
                </p>
              )}
              {settings?.contact?.whatsappNumber && (
                <p className="flex items-center gap-2 text-xs" style={{ color: textColor }}>
                  <Phone className="w-4 h-4 text-emerald-500 shrink-0" />
                  <span style={{ color: textColor }}>WhatsApp: {settings.contact.whatsappNumber}</span>
                </p>
              )}
              {settings?.contact?.email && (
                <p className="flex items-center gap-2 text-xs" style={{ color: textColor }}>
                  <Mail className="w-4 h-4 text-emerald-500 shrink-0" />
                  <span style={{ color: textColor }}>{settings.contact.email}</span>
                </p>
              )}

              {showWaChatBtn && (
                <div className="pt-2">
                  <a
                    href={`https://wa.me/${(settings?.contact?.whatsappNumber || '6282130955059').replace(/[^0-9]/g, '')}?text=Halo%20Tim%20Advokat%20ALY%20LAW%20FIRM,%20saya%20ingin%20berkonsultasi%20mengenai%20layanan%20hukum.`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 px-3.5 py-2 text-white rounded-xl text-xs font-bold shadow-xs transition-all hover:opacity-95"
                    style={{ backgroundColor: primaryColor }}
                  >
                    <span>{waChatBtnText}</span>
                    <ArrowUpRight className="w-3.5 h-3.5" />
                  </a>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Custom Disclaimer Box if provided */}

        {/* Bottom Bar: Copyright & Admin Link */}
        <div className="pt-6 sm:pt-8 flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-slate-500">
          <div className="flex items-center gap-2 text-center sm:text-left">
            <ShieldCheck className="w-4 h-4 text-emerald-500 shrink-0" />
            <span>
              &copy; {new Date().getFullYear()} {copyrightText}
            </span>
          </div>

          <div className="flex flex-wrap items-center justify-center gap-4">
            {showAdminLink && !hideLoginBtn && (
              <button
                id="footer-editor-login-link"
                onClick={() => navigate(user ? 'admin' : 'login')}
                className="hover:text-slate-300 flex items-center gap-1 transition-colors cursor-pointer"
              >
                <Lock className="w-3.5 h-3.5" />
                <span>{user ? 'Editorial Dashboard' : 'Admin Portal'}</span>
              </button>
            )}
          </div>
        </div>
      </div>
    </footer>
  );
}
